import { PageType } from "@product/scmp-sdk";
import { useRouter } from "next/router";
import { forwardRef } from "react";
import { graphql, useFragment } from "react-relay";

import type { DefaultComponentsProvider } from "~/components/content/content-item-render";
import { tracking } from "~/data";
import type { postiesSectionPrimaryContentItemContent$key } from "~/queries/__generated__/postiesSectionPrimaryContentItemContent.graphql";

import {
  Container,
  Content,
  DateContainer,
  Headline,
  StyledButton,
  StyledContentCoverImage,
  StyledEntityLink,
  StyledOffsetContainer,
} from "./styles";

export type Props = {
  className?: string;
  provider: DefaultComponentsProvider;
  reference: postiesSectionPrimaryContentItemContent$key;
};

export const ContentItemPostiesSectionPrimary = forwardRef<HTMLDivElement, Props>(
  ({ className, provider, reference: reference_ }, reference) => {
    const router = useRouter();

    const postiesSectionQueryParameters = {
      module: (router.query.name as string).toLowerCase().replaceAll(/[\s-]+/gi, "_"),
      pgtype: tracking.pageType.SectionPosties,
    };

    const content = useFragment(
      graphql`
        fragment postiesSectionPrimaryContentItemContent on Content {
          ...entityLink
          ...contentCoverImageContent
        }
      `,
      reference_,
    );

    return (
      <Container className={className} ref={reference}>
        <StyledEntityLink
          query={{ pageType: PageType.PostiesArticle, ...postiesSectionQueryParameters }}
          reference={content}
        >
          <StyledOffsetContainer borderRadius="8px">
            <StyledContentCoverImage
              reference={content}
              responsiveVariants={{
                desktopUp: "size1200x800",
                mobileUp: "size540x360",
                tabletUp: "size540x360",
              }}
            />
          </StyledOffsetContainer>
          <Content>
            <Headline>
              {provider.headline({
                hideTag: true,
                preferSocialHeadline: true,
              })}
            </Headline>
            <DateContainer>{provider.publishedDate({})}</DateContainer>
            <StyledButton variant="button">READ MORE</StyledButton>
          </Content>
        </StyledEntityLink>
      </Container>
    );
  },
);

ContentItemPostiesSectionPrimary.displayName = "ContentItemPostiesSectionPrimary";
